import { ExcelSheetReviewPanel } from '@/components/admin/data-sources/ExcelSheetReviewPanel';
import type {
  AppendExcelMappingRequest,
  AppendExcelParseResult,
  ExcelSheetCommit,
} from '@innermate/shared';
import { useMemo } from 'react';

/**
 * 추가 업로드 매핑 UI 의 행. 사용자가 시트별 매칭 모드를 선택:
 * - auto: 시트명 동일 → 그 테이블 (기본)
 * - existing: 사용자가 기존 테이블 dropdown 으로 선택
 * - new: 신규 테이블 생성 (newTableConfig)
 * - skip: 무시
 */
export type MappingMode = 'auto' | 'existing' | 'new' | 'skip';

export interface MappingRow {
  newSheetName: string;
  mode: MappingMode;
  /** mode=existing 일 때 사용자 선택 테이블 ID. */
  selectedTableId: string | null;
  /** mode=new 일 때 신규 테이블 commit 본. */
  newTableConfig: ExcelSheetCommit | null;
}

interface Props {
  parse: AppendExcelParseResult;
  /** 기존 데이터소스의 (id, sheetName, tableName) 목록. */
  existingTables: Array<{ id: string; sheetName: string; tableName: string }>;
  rows: MappingRow[];
  onChange: (next: MappingRow[]) => void;
}

export function ExcelSheetMappingTable({ parse, existingTables, rows, onChange }: Props) {
  const autoByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of parse.autoMapping) if (r.targetTableId) m.set(r.newSheetName, r.targetTableId);
    return m;
  }, [parse]);

  const updateRow = (idx: number, patch: Partial<MappingRow>) => {
    const next = rows.slice();
    const cur = next[idx];
    if (!cur) return;
    next[idx] = { ...cur, ...patch };
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-2xl border border-border bg-surface">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted/40 text-[11px] uppercase tracking-wider text-foreground-muted">
            <tr>
              <Th>새 시트</Th>
              <Th>매칭 모드</Th>
              <Th>대상</Th>
              <Th align="right">행수</Th>
            </tr>
          </thead>
          <tbody>
            {parse.sheets.map((sheet, idx) => {
              const row = rows[idx];
              if (!row) return null;
              const auto = autoByName.get(sheet.sheetName);
              return (
                <tr key={sheet.sheetName} className="border-t border-border align-top">
                  <td className="px-3 py-2.5">
                    <div className="font-medium">{sheet.sheetName}</div>
                    <div className="text-[10px] text-foreground-muted">
                      {sheet.columns.length} 컬럼
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <select
                      value={row.mode}
                      onChange={(e) => {
                        const mode = e.target.value as MappingMode;
                        if (mode === 'auto') {
                          updateRow(idx, { mode, selectedTableId: null, newTableConfig: null });
                        } else if (mode === 'existing') {
                          updateRow(idx, {
                            mode,
                            selectedTableId: existingTables[0]?.id ?? null,
                            newTableConfig: null,
                          });
                        } else if (mode === 'new') {
                          updateRow(idx, {
                            mode,
                            selectedTableId: null,
                            newTableConfig: makeDefaultCommitFromSheet(sheet),
                          });
                        } else {
                          updateRow(idx, { mode, selectedTableId: null, newTableConfig: null });
                        }
                      }}
                      className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
                    >
                      <option value="auto">자동 매핑 {auto ? '✓' : '(없음)'}</option>
                      <option value="existing">기존 테이블 선택</option>
                      <option value="new">신규 테이블 생성</option>
                      <option value="skip">무시</option>
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    {row.mode === 'auto' ? (
                      auto ? (
                        <span className="text-xs">
                          {existingTables.find((t) => t.id === auto)?.tableName ?? '(자동)'}
                        </span>
                      ) : (
                        <span className="text-xs text-amber-600">매칭되는 시트 없음 — skip 됨</span>
                      )
                    ) : row.mode === 'existing' ? (
                      <select
                        value={row.selectedTableId ?? ''}
                        onChange={(e) =>
                          updateRow(idx, { selectedTableId: e.target.value || null })
                        }
                        className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
                      >
                        <option value="">— 선택 —</option>
                        {existingTables.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.tableName} (시트: {t.sheetName})
                          </option>
                        ))}
                      </select>
                    ) : row.mode === 'new' ? (
                      <span className="text-xs text-foreground-muted">아래 패널에서 컬럼 검토</span>
                    ) : (
                      <span className="text-xs text-foreground-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-xs">
                    {sheet.rowCount.toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 신규 테이블 생성 시트별 컬럼 검토 패널. */}
      {parse.sheets.map((sheet, idx) => {
        const row = rows[idx];
        if (!row || row.mode !== 'new' || !row.newTableConfig) return null;
        return (
          <ExcelSheetReviewPanel
            key={`new-${sheet.sheetName}`}
            sheet={sheet}
            state={row.newTableConfig}
            onChange={(next) => updateRow(idx, { newTableConfig: next })}
          />
        );
      })}
    </div>
  );
}

export function buildAppendBodyFromRows(
  fileUploadId: string,
  rows: MappingRow[],
): AppendExcelMappingRequest {
  return {
    fileUploadId,
    mappings: rows
      .filter((r) => r.mode !== 'auto')
      .map((r) => ({
        newSheetName: r.newSheetName,
        targetTableId: r.mode === 'existing' ? r.selectedTableId : null,
        createNewTable: r.mode === 'new',
        newTableConfig: r.mode === 'new' ? r.newTableConfig : null,
      })),
  };
}

function makeDefaultCommitFromSheet(
  sheet: AppendExcelParseResult['sheets'][number],
): ExcelSheetCommit {
  const tableName = normalizeIdent(sheet.sheetName) || `sheet_${Math.floor(Math.random() * 1e6)}`;
  return {
    sheetName: sheet.sheetName,
    include: true,
    tableName: tableName.slice(0, 63),
    columns: sheet.columns.map((c) => {
      const fromHeader = c.rawHeader ? normalizeIdent(c.rawHeader) : '';
      const sqlName = (fromHeader.length > 0 ? fromHeader : c.sqlName).slice(0, 63);
      return {
        sqlName,
        parseSqlName: c.sqlName,
        label: c.rawHeader ?? '',
        description: null,
        type:
          c.detectedType === 'number'
            ? 'numeric'
            : c.detectedType === 'date'
              ? 'timestamptz'
              : c.detectedType === 'boolean'
                ? 'boolean'
                : 'text',
        include: c.detectedType !== 'empty',
      };
    }),
    pkColumns: [],
    dateColumn: null,
    dateFormat: null,
  };
}

/** 한글/공백 그대로 보존, 줄바꿈/탭/제어문자/큰따옴표만 제거. */
function normalizeIdent(raw: string): string {
  return raw.replace(/[ -]/g, '').replace(/"/g, '').replace(/\s+/g, ' ').trim();
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      scope="col"
      className={[
        'px-3 py-2.5 font-medium',
        align === 'right' ? 'text-right' : '',
        align === 'center' ? 'text-center' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </th>
  );
}
